// /shuttle/track — the page left open on a phone riding the campus shuttle.
//
// It does one thing: while "tracking" is on, send the phone's position to
// /api/shuttle-ping on the interval /admin → Shuttle sets. Everything else exists
// because a browser tab is a bad place to run something for eight hours, and
// the person who set it up will not be there when it stops.
//
// ── Why each piece is here ──────────────────────────────────────────────────
//  · SIGN-IN IS TWO STEPS: the shuttle password, then a code the server mails
//    to the UMaps inbox (never to an address typed here — api/shuttle-auth.js).
//    The phone is a dedicated device, so the token goes in localStorage and
//    survives the tab being reloaded mid-route; it lasts 16 hours and the admin
//    can revoke it. Any 401 from a ping drops it and shows the sign-in again.
//  · THE LATEST FIX EVERY TICK, NOT EVERY FIX. `watchPosition` can fire several
//    times a second with high accuracy on; the server refuses anything closer
//    than 10 s apart, and a position is only needed at the resolution the
//    phones poll at. Holding the newest fix and sending it on a timer is also
//    what lets a slow network never queue up a backlog of stale positions.
//  · THE SCREEN WAKE LOCK is the difference between this working and not: iOS
//    and Android both stop a page's geolocation and timers once the screen
//    sleeps. It is requested on Start (it needs a user gesture) and re-acquired
//    whenever the page becomes visible again, because the browser drops it every
//    time the tab is hidden. When the API is missing or refused, or the page has
//    been in the background, a RED banner says so — a tracker that has silently
//    stopped is the failure this page most needs to be loud about.
//  · The server's verdict on each ping is shown in words (accuracy, off route,
//    clock wrong…) because the person holding the phone is the only one who can
//    fix most of them.

import { useCallback, useEffect, useRef, useState } from 'react';
import UMapsWordmark from '../components/UMapsWordmark.jsx';

const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
const TOKEN_KEY = 'umaps-shuttle-token';
/**
 * The ping interval is the SERVER's, set in /admin → Shuttle: three things
 * have to agree about it (this timer, the "too soon" refusal, and what the
 * admin chose), and only one of them can be changed without touching the
 * phone on the bus. Every ping answer carries the current value, refusals
 * included, so a change reaches this page within one interval. This is only
 * the value to use until the first answer comes back.
 */
const SEND_EVERY_MS_DEFAULT = 20 * 1000;
/** Matches the server's MAX_FIX_AGE_MS: older than this is location stopped, not a parked bus. */
const MAX_FIX_AGE_MS = 10 * 60 * 1000;
/** Older than this, the page asks the browser for a fresh fix instead of waiting to be told. */
const REFRESH_AFTER_MS = 30 * 1000;

function readSaved() {
  try {
    const s = JSON.parse(localStorage.getItem(TOKEN_KEY) || 'null');
    if (s && typeof s.token === 'string' && Number(s.expiresAt) > Date.now()) return s;
  } catch { /* storage blocked or corrupt — sign in again */ }
  return null;
}
function writeSaved(v) {
  try {
    if (v) localStorage.setItem(TOKEN_KEY, JSON.stringify(v));
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* the session just will not survive a reload */ }
}

const ago = (ms) => {
  if (ms == null) return '—';
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s} s ago`;
  const m = Math.floor(s / 60);
  return m < 60 ? `${m} min ${s % 60} s ago` : `${Math.floor(m / 60)} h ${m % 60} min ago`;
};

async function postJson(path, body, token) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  return { status: res.status, json };
}

export default function TrackerPage() {
  const [session, setSession] = useState(readSaved);
  const [step, setStep] = useState('password'); // password → code (only while signed out)
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  const signOut = useCallback((why) => {
    writeSaved(null);
    setSession(null);
    setStep('password');
    setCode('');
    setErr(why || '');
  }, []);

  // A token that expires while the page sits open goes back to sign-in by
  // itself, rather than on the next refused ping.
  useEffect(() => {
    if (!session) return undefined;
    const left = Number(session.expiresAt) - Date.now();
    const id = setTimeout(() => signOut('The session expired — sign in again.'), Math.max(0, left));
    return () => clearTimeout(id);
  }, [session, signOut]);

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(''); setNote('');
    try {
      const r = await postJson('/api/shuttle-auth', { op: 'request', password });
      if (r.status === 200 && r.json.ok) {
        setPassword('');
        setStep('code');
        setNote('A code was sent to the UMaps inbox. Ask whoever has it for the six digits — it works once, for 10 minutes.');
      } else {
        setErr(r.json.error || 'That did not work.');
      }
    } catch {
      setErr('Could not reach the server. Check the connection.');
    }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await postJson('/api/shuttle-auth', { op: 'verify', code });
      if (r.status === 200 && r.json.token) {
        const s = { token: r.json.token, expiresAt: r.json.expiresAt };
        writeSaved(s);
        setSession(s);
        setNote('');
      } else {
        setErr(r.json.error || 'That code did not work.');
        if (r.json.restart) { setStep('password'); setCode(''); }
      }
    } catch {
      setErr('Could not reach the server. Check the connection.');
    }
    setBusy(false);
  };

  if (!session) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <div style={S.brand}><UMapsWordmark tool="Shuttle Tracker" size={28} href={null} /></div>
          {step === 'password' ? (
            <form onSubmit={requestCode} style={S.form}>
              <label style={S.label} htmlFor="shuttle-pw">Shuttle password</label>
              <input id="shuttle-pw" type="password" autoComplete="current-password" value={password}
                     onChange={(e) => setPassword(e.target.value)} style={S.input} autoFocus />
              {err && <div style={S.err}>{err}</div>}
              <button type="submit" style={S.primary} disabled={busy || !password}>
                {busy ? 'Checking…' : 'Continue'}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} style={S.form}>
              {note && <div style={S.note}>{note}</div>}
              <label style={S.label} htmlFor="shuttle-code">Six-digit code</label>
              <input id="shuttle-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                     value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                     style={{ ...S.input, ...S.codeInput }} autoFocus />
              {err && <div style={S.err}>{err}</div>}
              <button type="submit" style={S.primary} disabled={busy || code.length !== 6}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <button type="button" style={S.ghost} onClick={() => { setStep('password'); setErr(''); setNote(''); }}>
                Start again
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return <Tracker token={session.token} expiresAt={session.expiresAt} onSignOut={signOut} />;
}

function Tracker({ token, expiresAt, onSignOut }) {
  const [tracking, setTracking] = useState(false);
  const [fix, setFix] = useState(null);          // newest from watchPosition
  const [geoErr, setGeoErr] = useState('');
  const [last, setLast] = useState(null);        // { at, accepted, reason, message }
  const [counts, setCounts] = useState({ sent: 0, accepted: 0, refused: 0 });
  const [everyMs, setEveryMs] = useState(SEND_EVERY_MS_DEFAULT);
  const [wake, setWake] = useState('off');       // off | on | unsupported | refused | released
  const [backgrounded, setBackgrounded] = useState(false);
  const [now, setNow] = useState(Date.now());

  const fixRef = useRef(null);
  const sentTRef = useRef(0);
  const lockRef = useRef(null);
  const trackingRef = useRef(false);

  const acquireWake = useCallback(async () => {
    if (!('wakeLock' in navigator)) { setWake('unsupported'); return; }
    try {
      const lock = await navigator.wakeLock.request('screen');
      lockRef.current = lock;
      setWake('on');
      lock.addEventListener('release', () => {
        if (lockRef.current === lock) lockRef.current = null;
        if (trackingRef.current) setWake('released');
      });
    } catch {
      setWake('refused');
    }
  }, []);

  const send = useCallback(async () => {
    // EVERY tick, not only when a new fix arrives. A phone that is not moving
    // — a bus waiting at a stop, a Mac on a desk — stops producing fixes, and
    // sending nothing made the bus vanish from every map three minutes into
    // any wait. So the last fix is re-sent, stamped with when it was SENT (`t`)
    // and when it was MEASURED (`fixT`); the server judges the second by age.
    const f = fixRef.current;
    if (!f) return;
    const t = Date.now();
    if (t - f.t > MAX_FIX_AGE_MS) {
      setLast({ at: t, accepted: false, reason: 'old-fix',
        message: 'No new location from this device for over 10 minutes — check that Location is on.' });
      return;
    }
    sentTRef.current = t;
    try {
      const r = await postJson('/api/shuttle-ping', {
        lat: f.lat, lng: f.lng, accuracy: f.accuracy, t, fixT: f.t,
        ...(Number.isFinite(f.speed) ? { speed: f.speed } : {}),
        ...(Number.isFinite(f.heading) ? { heading: f.heading } : {}),
      }, token);
      if (r.status === 401) {
        onSignOut(r.json.message || 'Signed out — sign in again.');
        return;
      }
      const accepted = !!r.json.accepted;
      const every = Number(r.json.everySec);
      if (Number.isFinite(every) && every >= 5) setEveryMs(every * 1000);
      setLast({ at: Date.now(), accepted, reason: r.json.reason, message: r.json.message || r.json.error || '' });
      setCounts((c) => ({ sent: c.sent + 1, accepted: c.accepted + (accepted ? 1 : 0), refused: c.refused + (accepted ? 0 : 1) }));
    } catch {
      setLast({ at: Date.now(), accepted: false, reason: 'network', message: 'No connection — the next ping will try again.' });
      setCounts((c) => ({ ...c, sent: c.sent + 1, refused: c.refused + 1 }));
    }
  }, [token, onSignOut]);

  // Geolocation + the send timer, for as long as tracking is on.
  useEffect(() => {
    trackingRef.current = tracking;
    if (!tracking) return undefined;
    if (!('geolocation' in navigator)) {
      setGeoErr('This browser has no location access.');
      return undefined;
    }
    let first = true;
    const onFix = (p, measuredNoEarlierThan = 0) => {
      const f = {
        lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy,
        speed: p.coords.speed, heading: p.coords.heading,
        t: Math.max(p.timestamp || Date.now(), measuredNoEarlierThan),
      };
      fixRef.current = f;
      setFix(f);
      setGeoErr('');
      // Send the very first fix straight away, so Start is confirmed within
      // seconds rather than after the first 20-second tick.
      if (first) { first = false; send(); }
    };
    const onErr = (e) => setGeoErr(e.code === 1
      ? 'Location permission was denied. Allow it for this site in the browser settings.'
      : `Location error: ${e.message || 'unavailable'}`);
    const watch = navigator.geolocation.watchPosition(
      (p) => onFix(p), onErr, { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 },
    );
    // `watchPosition` only reports when the browser thinks something CHANGED.
    // A device that is not moving — a Mac on a desk, the bus phone at a stop —
    // measures once and then goes quiet, and after ten minutes the fix looks
    // like location switched off. So once the newest fix is REFRESH_AFTER_MS
    // old, ask for a fresh one outright. `maximumAge: 0` means the answer was
    // measured after the ask, so it is stamped no earlier than that — some
    // browsers hand back the old timestamp on an unchanged position.
    let asking = false;
    const refresh = () => {
      const f = fixRef.current;
      if (asking || (f && Date.now() - f.t < REFRESH_AFTER_MS)) return;
      asking = true;
      const askedAt = Date.now();
      navigator.geolocation.getCurrentPosition(
        (p) => { asking = false; onFix(p, askedAt); },
        (e) => { asking = false; if (e.code === 1) onErr(e); },
        { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
      );
    };
    const timer = setInterval(() => { refresh(); send(); }, everyMs);
    return () => {
      navigator.geolocation.clearWatch(watch);
      clearInterval(timer);
    };
  }, [tracking, send, everyMs]);

  // The browser drops a wake lock whenever the page is hidden. Take it back on
  // return, and remember that the page WAS hidden — pings may have stopped.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        if (trackingRef.current) setBackgrounded(true);
      } else if (trackingRef.current && !lockRef.current) {
        acquireWake();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [acquireWake]);

  // A clock for the "N s ago" labels.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const start = () => {
    setTracking(true);
    setBackgrounded(false);
    acquireWake();
  };
  const stop = () => {
    setTracking(false);
    setWake('off');
    const lock = lockRef.current;
    lockRef.current = null;
    if (lock) lock.release().catch(() => {});
  };

  useEffect(() => () => { lockRef.current?.release?.().catch?.(() => {}); }, []);

  const wakeBad = tracking && wake !== 'on';
  const stale = tracking && last && now - last.at > 60 * 1000;

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.head}>
          <UMapsWordmark tool="Shuttle Tracker" size={24} href={null} />
          <button style={S.small} onClick={() => { stop(); onSignOut(''); }}>Sign out</button>
        </div>

        {wakeBad && (
          <div style={S.banner}>
            {wake === 'unsupported' && 'This browser cannot keep the screen awake. Set Auto-Lock to Never, or the tracker stops when the screen sleeps.'}
            {wake === 'refused' && 'The browser refused to keep the screen awake. Set Auto-Lock to Never, or the tracker stops when the screen sleeps.'}
            {wake === 'released' && 'The screen wake lock was lost. Tap here to take it back.'}
            {wake === 'off' && 'Acquiring the screen wake lock…'}
            {wake === 'released' && <button style={S.bannerBtn} onClick={acquireWake}>Keep screen on</button>}
          </div>
        )}
        {tracking && backgrounded && (
          <div style={S.banner}>
            This page was in the background — pings may have stopped while it was hidden. Keep this tab open and on screen.
            <button style={S.bannerBtn} onClick={() => setBackgrounded(false)}>OK</button>
          </div>
        )}
        {geoErr && <div style={S.banner}>{geoErr}</div>}

        <button style={{ ...S.big, ...(tracking ? S.bigStop : S.bigStart) }} onClick={tracking ? stop : start}>
          {tracking ? 'Stop tracking' : 'Start tracking'}
        </button>
        <div style={S.statusLine}>
          {tracking ? `Sending the bus's position every ${Math.round(everyMs / 1000)} seconds.` : 'Not tracking. The bus is not being shown from this phone.'}
        </div>

        <div style={S.grid}>
          <Cell label="Last sent" value={last ? ago(now - last.at) : '—'} warn={stale} />
          <Cell label="GPS accuracy" value={fix ? `${Math.round(fix.accuracy)} m` : '—'} warn={fix && fix.accuracy > 100} />
          <Cell label="Sent" value={counts.sent} />
          <Cell label="Accepted / refused" value={`${counts.accepted} / ${counts.refused}`} />
        </div>

        {last && (
          <div style={{ ...S.verdict, ...(last.accepted ? S.verdictOk : S.verdictBad) }}>
            {last.accepted ? 'Server accepted the last position.' : `Server refused the last position: ${last.message || last.reason}`}
          </div>
        )}

        <div style={S.tips}>
          <div style={S.tipsTitle}>Setting up the phone</div>
          <ul style={S.tipsList}>
            <li><b>Keep it charging.</b> High-accuracy GPS with the screen on drains a battery in a few hours.</li>
            <li><b>Auto-Lock: Never</b> (iPhone: Settings → Display &amp; Brightness; Android: Display → Screen timeout, longest).</li>
            <li><b>Guided Access</b> (iPhone: Settings → Accessibility → Guided Access, then triple-click the side button on this page) or <b>App pinning</b> on Android, so the tab cannot be switched away from.</li>
            <li>Allow <b>precise location</b> for this site, and keep this tab in front. Do not switch apps.</li>
            <li>Set the phone's clock to <b>set automatically</b> — pings with a wrong clock are refused.</li>
          </ul>
          <div style={S.expiry}>Signed in until {new Date(Number(expiresAt)).toLocaleString('en-CA', { hour: 'numeric', minute: '2-digit', weekday: 'short' })}.</div>
        </div>
      </div>
    </div>
  );
}

function Cell({ label, value, warn }) {
  return (
    <div style={{ ...S.cell, ...(warn ? S.cellWarn : {}) }}>
      <div style={S.cellValue}>{value}</div>
      <div style={S.cellLabel}>{label}</div>
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', background: '#f1f3f4', color: '#202124', fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif', padding: 16, boxSizing: 'border-box' },
  wrap: { maxWidth: 520, margin: '0 auto' },
  card: { maxWidth: 380, margin: '10vh auto 0', background: '#fff', padding: 24, borderRadius: 16, boxShadow: '0 6px 28px rgba(0,0,0,0.14)' },
  brand: { marginBottom: 18 },
  form: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: 14, fontWeight: 700, marginBottom: 6 },
  input: { fontSize: 18, padding: '14px 14px', border: '1.5px solid #dadce0', borderRadius: 12, outline: 'none', marginBottom: 14 },
  codeInput: { fontSize: 30, letterSpacing: 8, textAlign: 'center', fontWeight: 800 },
  primary: { background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 12, padding: 16, fontSize: 17, fontWeight: 800, cursor: 'pointer', minHeight: 52 },
  ghost: { background: 'none', color: '#1a73e8', border: 'none', padding: 14, fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 4 },
  err: { background: '#fce8e6', color: '#c5221f', borderRadius: 10, padding: '10px 12px', fontSize: 14, marginBottom: 12 },
  note: { background: '#e8f0fe', color: '#174ea6', borderRadius: 10, padding: '10px 12px', fontSize: 14, lineHeight: 1.45, marginBottom: 14 },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  small: { background: '#fff', color: '#202124', border: '1.5px solid #dadce0', borderRadius: 10, padding: '10px 14px', fontSize: 14, fontWeight: 700, cursor: 'pointer', minHeight: 44 },
  banner: { background: '#d93025', color: '#fff', borderRadius: 12, padding: '14px 16px', fontSize: 15, fontWeight: 700, lineHeight: 1.4, marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 10 },
  bannerBtn: { alignSelf: 'flex-start', background: '#fff', color: '#c5221f', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 15, fontWeight: 800, cursor: 'pointer', minHeight: 44 },
  big: { width: '100%', border: 'none', borderRadius: 20, padding: '34px 16px', fontSize: 28, fontWeight: 900, color: '#fff', cursor: 'pointer', boxShadow: '0 6px 20px rgba(0,0,0,0.16)', marginTop: 4 },
  bigStart: { background: '#188038' },
  bigStop: { background: '#c5221f' },
  statusLine: { textAlign: 'center', color: '#5f6368', fontSize: 14, margin: '10px 0 16px' },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 },
  cell: { background: '#fff', borderRadius: 14, padding: '14px 12px', border: '1px solid #dadce0' },
  cellWarn: { borderColor: '#d93025', background: '#fce8e6' },
  cellValue: { fontSize: 22, fontWeight: 800 },
  cellLabel: { fontSize: 12, fontWeight: 700, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 2 },
  verdict: { borderRadius: 12, padding: '12px 14px', fontSize: 15, fontWeight: 700, marginBottom: 14 },
  verdictOk: { background: '#e6f4ea', color: '#137333' },
  verdictBad: { background: '#fef7e0', color: '#b06000' },
  tips: { background: '#fff', borderRadius: 14, padding: '14px 16px', border: '1px solid #dadce0' },
  tipsTitle: { fontSize: 12, fontWeight: 800, color: '#5f6368', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 },
  tipsList: { margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.55 },
  expiry: { fontSize: 13, color: '#5f6368', marginTop: 10 },
};
